import { Camera, Trash2 } from "lucide-react";
import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";

import { NzrAvatar } from "nazare-ui/components";
import { PageSection } from "nazare-ui/components/page-section";

import { useProfile, useUpdateProfile } from "../profile-api";
import { SkeletonBlock } from "../skeleton-block";
import { useDocumentTitle } from "../use-document-title";
import { AvatarUploadModal } from "./avatar-upload-modal";
import styles from "./profile.module.css";

const MAX_SOURCE_IMAGE_BYTES = 15 * 1024 * 1024;

/**
 * `/profile` — edit-your-own-name-and-avatar page, reached via the
 * identity control at the bottom of the nav rail. Backed by the
 * `notifications-admin` BFF worktree's self-scoped `/user/me` routes
 * (see `../profile-api.ts`) — NOT `useStoredAuth()`'s `user` object, which
 * is a separate, read-only localStorage source that `root-layout.tsx`'s
 * `useIdentity()` reads for the nav rail's own display name and is left
 * untouched here; this page only edits+persists the BFF's own copy.
 *
 * No on-page `<h1>` — the breadcrumb trail already reads "Profile", same
 * convention as every other page in this app.
 */
export function ProfilePage() {
  useDocumentTitle("Profile");

  const { data, isPending, isError } = useProfile();
  const updateProfile = useUpdateProfile();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [avatar, setAvatar] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Seed the form from the fetched profile once it lands. A plain effect
  // (not `defaultValue`) since the fetch is async and this is a controlled
  // form — re-syncs if the underlying profile id changes (e.g. re-fetched
  // after `useUpdateProfile`'s own cache invalidation).
  useEffect(() => {
    if (!data?.profile) return;
    setFirstName(data.profile.first_name ?? "");
    setLastName(data.profile.last_name ?? "");
    setAvatar(data.profile.avatar ?? null);
  }, [data?.profile]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setStatus("idle");
    // `avatar` is omitted (not merely `undefined`-valued) — the BFF's
    // UpdateUserSchema treats an omitted field as "leave unchanged", so the
    // name-only Save button here never touches whatever the photo actions
    // below already persisted.
    updateProfile.mutate(
      { firstName, lastName },
      {
        onSuccess: () => setStatus("success"),
        onError: () => setStatus("error"),
      },
    );
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so picking the same file again still fires onChange
    if (!file) return;

    setAvatarError(null);
    if (!file.type.startsWith("image/")) {
      setAvatarError("Choose an image file.");
      return;
    }
    if (file.size > MAX_SOURCE_IMAGE_BYTES) {
      setAvatarError("Image is too large (max 15MB).");
      return;
    }
    setPendingFile(file);
  };

  // Saves a photo change independently of the name fields' own Save button
  // — uses the server's last-known name values (not the live form state),
  // so editing your name and then changing your photo before hitting Save
  // never silently commits the in-progress name edit as a side effect.
  const saveAvatar = (nextAvatar: string | null) => {
    if (!data?.profile) return;
    setAvatarError(null);
    updateProfile.mutate(
      { firstName: data.profile.first_name, lastName: data.profile.last_name, avatar: nextAvatar },
      {
        onSuccess: () => setAvatar(nextAvatar),
        onError: () => setAvatarError("Couldn't save — try again."),
      },
    );
  };

  if (isPending) {
    return (
      <div className={styles.page}>
        <p className={styles.description}>Update your name and avatar.</p>
        <PageSection padded aria-label="Profile" aria-busy="true">
          <div className={styles.formContent}>
            <div className={styles.avatarRow}>
              <SkeletonBlock width={64} height={64} radius="var(--radius-full)" />
              <SkeletonBlock width={140} height={13} />
            </div>
            <div className={styles.fieldGrid}>
              <SkeletonBlock height={36} />
              <SkeletonBlock height={36} />
            </div>
          </div>
        </PageSection>
      </div>
    );
  }

  if (isError || !data?.profile) {
    return (
      <div className={styles.page}>
        <p className={styles.description}>Couldn't load your profile. Try reloading the page.</p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <p className={styles.description}>Update your name and avatar.</p>
      <PageSection padded aria-label="Profile">
        <form className={styles.formContent} onSubmit={handleSubmit}>
          <div className={styles.avatarRow}>
            <button
              type="button"
              className={styles.avatarButton}
              onClick={() => fileInputRef.current?.click()}
              aria-label={avatar ? "Change profile photo" : "Upload profile photo"}
            >
              <NzrAvatar
                name={[firstName, lastName].filter(Boolean).join(" ")}
                imageUrl={avatar}
                size={64}
              />
              <span className={styles.avatarButtonOverlay} aria-hidden="true">
                <Camera size={16} />
              </span>
            </button>

            <div className={styles.avatarActions}>
              <div className={styles.avatarActionButtons}>
                <button type="button" className={styles.avatarActionButton} onClick={() => fileInputRef.current?.click()}>
                  <Camera size={13} aria-hidden="true" />
                  {avatar ? "Change photo" : "Upload photo"}
                </button>
                {avatar && (
                  <button
                    type="button"
                    className={`${styles.avatarActionButton} ${styles["avatarActionButton--danger"]}`}
                    onClick={() => saveAvatar(null)}
                    disabled={updateProfile.isPending}
                  >
                    <Trash2 size={13} aria-hidden="true" />
                    Remove
                  </button>
                )}
              </div>
              <p className={styles.avatarHint}>JPG or PNG. You'll be able to crop it before saving.</p>
              {avatarError && <p className={`${styles.status} ${styles["status--error"]}`}>{avatarError}</p>}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className={styles.hiddenFileInput}
              onChange={handleFileChange}
            />
          </div>

          <div className={styles.fieldGrid}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="profile-first-name">
                First name
              </label>
              <input
                id="profile-first-name"
                type="text"
                className={styles.input}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="profile-last-name">
                Last name
              </label>
              <input
                id="profile-last-name"
                type="text"
                className={styles.input}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>
          </div>

          <div className={styles.actionsRow}>
            <button type="submit" className={styles.saveButton} disabled={updateProfile.isPending}>
              {updateProfile.isPending ? "Saving…" : "Save"}
            </button>
            {status === "success" && <p className={`${styles.status} ${styles["status--success"]}`}>Saved.</p>}
            {status === "error" && (
              <p className={`${styles.status} ${styles["status--error"]}`}>Couldn't save — try again.</p>
            )}
          </div>
        </form>
      </PageSection>

      {pendingFile && (
        <AvatarUploadModal
          file={pendingFile}
          onCancel={() => setPendingFile(null)}
          onConfirm={(dataUrl) => {
            setPendingFile(null);
            saveAvatar(dataUrl);
          }}
        />
      )}
    </div>
  );
}
